// ПОЛЗУНОК ПАНЕЛИ A (ШАГ 3) — арифметика дорожки, клавиатуры и того, что рисуется. B5-3.
//
// ИСТОЧНИК — `<script>` борда `Life Map - s3 Deck v8.html`: `rowHTML` (`:475–487`) даёт положение
// (`pos=(v+3)/6*100`), геометрию полосы (`left=min(50,pos)`, `width=|pos−50|`) и зоны полюсов
// (`v<-1?'l':(v>1?'r':'c')`); обработчик `pointerdown` (`:537–549`) — перевод координаты в
// значение с зажимом в ∓3 и округлением до двух знаков. Клавиатуры у борда НЕТ ВОВСЕ — см. ниже.
//
// ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ, как `deckGate.js`: это утверждения о ЗНАЧЕНИИ, и проверять их надо без
// монтирования, без браузера и без указателя. Плюс своя причина: одно и то же значение читают
// четверо — дорожка (положение), полюса (подсветка), `aria-valuetext` (озвучка) и гейт (счёт), — и
// если перевод «значение → вид» живёт в замыкании экрана, четвёртый читатель получает свою копию.
//
// ⛔ ДЕЛЕНИЙ НЕТ (`s508.d4`). Значение — float, шаг существует только у КЛАВИАТУРЫ, и это не
// «скрытая сетка»: указателем по-прежнему берётся любая точка дорожки. Число `KEY_STEP` нельзя
// выводить из вида — у борда его нет ни в байте, ни в разметке.
//
// ⚠️ КЛАВИАТУРА — ПРЕДЛОЖЕНИЕ CC, А НЕ ТРЕБОВАНИЕ БОРДА (§19 спеки деки — долг). У борда дорожка
// это `<div>` с `pointerdown`: с клавиатуры до неё не добраться вовсе, то есть весь обязательный
// ввод визарда для клавиатуры сегодня закрыт. Поэтому здесь появляются три числа, которых в каноне
// нет (`KEY_STEP`, `KEY_STEP_BIG` и выбор базы у нетронутого), и каждое названо своим именем, чтобы
// CD и web-Claude правили их как решение, а не искали в борде.
//
// ⛔ ФОКУС И ПЕРЕХОД КАСАНИЕМ ЗНАЧЕНИЯ НЕ СТАВЯТ. В этом файле это выражено формой: нет ни одной
// функции, которая возвращала бы значение БЕЗ названного жеста. `keyStep` на чужой клавише отвечает
// `null` — «не моё», и вызывающий не пишет ничего. Иначе пол гейта обходился бы табом.

import { A11Y_COPY } from './deckCopy.js';

/** Границы дорожки (борд: зажим `Math.max(-3,Math.min(3,…))`). */
export const SLIDER_MIN = -3;
export const SLIDER_MAX = 3;

/** Положение нетронутой дорожки в процентах — середина (борд: `pos=isN?…:50`). */
export const REST_PERCENT = 50;

/**
 * Шаг стрелки и шаг `PageUp`/`PageDown` — ЧИСЛА CC, в каноне их нет.
 *
 * `0.5` выбран так, чтобы с клавиатуры была достижима вся ширина (13 остановок на шести единицах)
 * и при этом хранимое значение осталось тем же float, что кладёт указатель. `1` — крупный шаг,
 * чтобы дойти до полюса за три нажатия, а не за шесть.
 */
export const KEY_STEP = 0.5;
export const KEY_STEP_BIG = 1;

/** Округление борда — два знака (`Math.round(v*100)/100`). */
const round2 = (v) => Math.round(v * 100) / 100;

/** Зажим в ∓3, как в обработчике борда. */
const clamp = (v) => Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, v));

/**
 * Сведён ли слой: конечное число внутри ∓3.
 *
 * ⚠️ ДВА РАЗНЫХ ВОПРОСА, И ЭТО НЕ ПРИДИРКА. `deckGate.nSliders` считает ТРОНУТОСТЬ (`!== null`) и
 * диапазон не смотрит — так решено в `s443.d5` и так проверяет конверт (`step3RowsOK`: тип, не
 * граница). Здесь спрашивается другое: есть ли у значения место на дорожке. Конверт из чужой вкладки
 * или из будущей версии может принести `5`, и рисовать его негде — рекомендация CC из приёмки B5-1,
 * вопрос 3.
 */
export const drawable = (v) => typeof v === 'number' && Number.isFinite(v)
  && v >= SLIDER_MIN && v <= SLIDER_MAX;

/** Положение значения в процентах ширины дорожки (борд: `(v+3)/6*100`). */
export const percentOf = (v) => ((v - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * 100;

/**
 * Полоса от середины к значению (борд: `left:min(50,pos)`, `width:|pos−50|`).
 *
 * Возвращает проценты числами, а не строку со `%`: строку собирает разметка, и держать её здесь
 * значило бы решать за неё формат.
 */
export function barOf(percent) {
  return { left: Math.min(REST_PERCENT, percent), width: Math.abs(percent - REST_PERCENT) };
}

/**
 * Зона полюса под значением (борд: `v<-1?'l':(v>1?'r':'c')`). `null` — рисовать нечего.
 *
 * ⚠️ ГРАНИЦЫ РОВНО КАК В БОРДЕ, ВКЛЮЧАЯ СТРОГОСТЬ: `-1` попадает в середину, а не в левый полюс
 * (`v<-1` ложно при `v === -1`). Значение на границе — самый частый способ разъехаться с бордом
 * на единицу, поэтому оно стоит в таблице теста отдельной строкой.
 */
export function hotPole(v) {
  if (!drawable(v)) return null;
  if (v < -1) return 'l';
  if (v > 1) return 'r';
  return 'c';
}

/**
 * Координата указателя → значение (борд `:539`): доля ширины, растянутая на шесть единиц, зажатая
 * в ∓3 и округлённая до двух знаков.
 *
 * @param {number} clientX координата события
 * @param {{left: number, width: number}} rect прямоугольник дорожки
 */
export function fromPointer(clientX, rect) {
  if (!rect || !rect.width) return 0;
  const raw = ((clientX - rect.left) / rect.width) * (SLIDER_MAX - SLIDER_MIN) + SLIDER_MIN;
  return round2(clamp(raw));
}

/**
 * Клавиша → новое значение. `null` = клавиша не наша, писать нечего.
 *
 * БАЗА У НЕТРОНУТОГО — СЕРЕДИНА, и это решение CC, а не находка: дорожка нетронутого слоя стоит на
 * `50%`, то есть человек видит центр, и первая стрелка обязана двинуться ОТ ТОГО, ЧТО ВИДНО. Тот же
 * ответ покрывает значение вне ∓3 из восстановленного конверта: клавиша его РЕМОНТИРУЕТ, приводя к
 * центру со сдвигом, вместо того чтобы считать от числа, которое нигде не нарисовано.
 *
 * ⛔ `Home`/`End` СТАВЯТ ПОЛЮС, А НЕ «СБРАСЫВАЮТ». Сброса в `null` с клавиатуры нет вовсе: снятие
 * тронутости — отдельный предмет, которого нет ни в борде, ни в спеке, и выдумывать его здесь
 * значило бы дать клавиатуре ход, которого нет у указателя.
 */
export function keyStep(key, value) {
  const base = drawable(value) ? value : 0;
  switch (key) {
    case 'ArrowRight':
    case 'ArrowUp':
      return round2(clamp(base + KEY_STEP));
    case 'ArrowLeft':
    case 'ArrowDown':
      return round2(clamp(base - KEY_STEP));
    case 'PageUp':
      return round2(clamp(base + KEY_STEP_BIG));
    case 'PageDown':
      return round2(clamp(base - KEY_STEP_BIG));
    case 'Home':
      return SLIDER_MIN;
    case 'End':
      return SLIDER_MAX;
    default:
      return null;
  }
}

/**
 * Озвучка значения — ПОДПИСЬ ПОЛЮСА БОРДА, а не своя формулировка.
 *
 * ⛔ ПОЧЕМУ НЕ «3 из 7» И НЕ «слегка влево». Текст в UI — зона web-Claude (CLAUDE.md), а `aria-`
 * строка это текст, который человек слышит. Взять его можно только из того, что уже написано, и
 * такая строка есть: борд сам называет зону значения, подсвечивая один из трёх полюсов. Поэтому
 * озвучка = ровно та подпись, которая в этот момент горит на экране.
 *
 * ⭐ У НЕСВЕДЁННОЙ ДОРОЖКИ ОЗВУЧКА ТЕПЕРЬ ЕСТЬ — `A11Y_COPY.sliderUnset`, копи web-Claude от 26.09.
 * До этого здесь возвращался `null` и слот стоял открытым: строки «не сведён» в каноне не было, а
 * сочинять UI-копи единолично нельзя. Слот закрыт, дыра снята.
 *
 * ⚠️ ЗНАЧЕНИЕ ВНЕ ∓3 ОЗВУЧИВАЕТСЯ ТАК ЖЕ, КАК НЕСВЕДЁННОЕ, и это выбор, а не побочка. На дорожке
 * его не рисуется ничего (приёмка B5-1, вопрос 3) — значит слышно обязано быть то же, что видно,
 * иначе экран и озвучка разойдутся на одном и том же ряду. Цена названа: счётчик `{n}/7` такое
 * значение СЧИТАЕТ, то есть озвучка расходится со счётчиком. Дом этого расхождения — шов сабмита
 * (блок 10), а не строка `aria-`.
 *
 * @param {{poles: {left: string, mid: string, right: string}}} layer запись `LAYER_COPY`
 * @param {number|null} v значение слоя
 */
export function valueText(layer, v) {
  const hot = hotPole(v);
  if (!hot) return A11Y_COPY.sliderUnset;
  if (!layer || !layer.poles) return null;
  if (hot === 'l') return layer.poles.left;
  if (hot === 'r') return layer.poles.right;
  return layer.poles.mid;
}
